import { ApplicationRef, Injectable, Injector } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { FlushToastAction, SetToastAction, ShowToastAction } from './toast.actions';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { getToasts } from './toast.selectors';
import { Toast } from '@core/models/toast/toast';
import { ToastType } from '@core/models/toast/toast-type';
import { ToastOverlayComponent } from '@components/toast/toast-overlay.component';
import { ApiErrorResponse } from '@core/data/api/error-response';
import { ToastStateModule } from './toast-state.module';

/**
 * Manages application toast / status notifications.
 */
@Injectable({ providedIn: ToastStateModule })
export class ToastService {

    private isOverlay: boolean = false;
    private overlayRef: OverlayRef;

    constructor(private store$: Store<any>,
                private appRef: ApplicationRef,
                private overlayService: Overlay,
                private injector: Injector
            ) {

        // Init toast overlay
        const positionStrategy = this.overlayService.position()
          .global()
          .centerHorizontally()
          .centerVertically();

        const overlayConfig = new OverlayConfig({
            hasBackdrop: false,
            panelClass: 'c-overlay-toast',
            scrollStrategy: this.overlayService.scrollStrategies.block(),
            positionStrategy
        });

        this.store$.select(getToasts)
            .subscribe((toast) => {
                if (toast) {

                    const overlayRef = this.overlayService.create(overlayConfig);
                    // const injectionTokens = new WeakMap();
                    // let portalInjector = new PortalInjector(this.injector, injectionTokens);
                    let toastPortal = new ComponentPortal(ToastOverlayComponent, null, this.injector);

                    // Attach overlay on first notification only
                    overlayRef.attach(toastPortal);

                    const timer = setTimeout(() => {
                        overlayRef.dispose();
                        appRef.tick();
                        clearTimeout(timer);
                    }, 5000);
                }
            });
        }

    /**
     * Returns all toasts.
     * @returns {Observable<R>}
     */
    getToasts(): Observable<any> {
        return this.store$.select(getToasts);
    }

    /**
     * Creates a new show error action.
     * @param error
     * @returns {SetToastAction}
     */
    showErrorAction(errorResponse: any): Action {
        let errorText: string = 'Errors.Unknown';

        // if error is thrown from backend, the api response would contain error info structured
        // in this format
        if (errorResponse && errorResponse.error && errorResponse.error.error) {
            errorText = errorResponse.error.error;
        }

        return new SetToastAction(new Toast(ToastType.ERROR, errorText, errorResponse && errorResponse.error));
    }

    /**
     * Use this when showing the toast from inside a component from the observable error handler
     * since it needs to show an error returned by the api
     *
     * @param {ApiErrorResponse} errorResponse
     */
    showApiError(errorResponse: { payload: ApiErrorResponse }) {
        let errorText: string = 'Errors.Unknown';
        const payload: ApiErrorResponse = errorResponse && errorResponse.payload;
        // if error is thrown from backend, the api response would contain error info structured
        // in this format
        if (payload && payload.error && payload.error.error) {
            errorText = payload.error.error;
        }

        this.toast(errorText, ToastType.ERROR, payload);
    }

    showError(msg: string, error?: any, subtitle?: string) {
        this.toast(error ? error.error : msg, ToastType.ERROR, error, subtitle);
    }

    showInfo(msg: string, error?: any, subtitle?: string) {
        this.toast(msg, ToastType.INFO, error, subtitle);
    }

    showWarning(msg: string, error?: any, subtitle?: string) {
        this.toast(error ? error.error : msg, ToastType.WARNING, error, subtitle);
    }

    showSuccess(msg: string, error?: any, subtitle?: string) {
        this.toast(error ? error.error : msg, ToastType.SUCCESS, error, subtitle);
    }

    flushToast() {
        return this.store$.dispatch(new FlushToastAction());
    }

    private toast(msg: string, type?: ToastType, error?: any, subtitle?: string) {

        const toast = new Toast((type ? type : ToastType.INFO), msg, error, subtitle);
        return this.store$.dispatch(new SetToastAction(toast));
    }

    private show(type: ToastType, msg: string, error?: any, subtitle?: string) {
        this.store$.dispatch(new ShowToastAction(new Toast(type, msg, error, subtitle)));
    }
}
